// src/components/AGTUps/services/AGTUps-product-services/strategyService.js

import { api } from './config';

/**
 * Servicio para gestionar estrategias de crecimiento
 */
const strategyService = {
  /**
   * Generar análisis inicial de estrategias usando IA
   * @returns {Promise<Object>} Resultado del análisis
   */
  generateAnalysis: async () => {
    try {
      const response = await api.post('/strategy/analyze');
      return response.data;
    } catch (error) {
      console.error('[strategyService] Error generating analysis:', error);
      throw new Error(error.response?.data?.error || 'Error generando análisis');
    }
  },

  /**
   * Obtener todas las estrategias del usuario
   * @param {Object} params Parámetros de consulta
   * @param {string} [params.status] Filtrar por estado
   * @param {string} [params.category] Filtrar por categoría
   * @param {string} [params.sort] Campo para ordenar
   * @returns {Promise<Object>} Lista de estrategias
   */
  getUserStrategies: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.category) queryParams.append('category', params.category);
      if (params.sort) queryParams.append('sort', params.sort);

      const response = await api.get(`/strategy/user-strategies?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('[strategyService] Error fetching strategies:', error);
      throw new Error(error.response?.data?.error || 'Error obteniendo estrategias');
    }
  },

  /**
   * Actualizar progreso de una estrategia
   * @param {string} strategyId ID de la estrategia
   * @param {Array<number>} completedStepIds IDs de pasos completados
   * @param {Object} [notes] Notas por paso
   * @returns {Promise<Object>} Estrategia actualizada
   */
  updateProgress: async (strategyId, completedStepIds, notes = {}) => {
    try {
      const response = await api.put('/strategy/progress', {
        strategyId,
        completedStepIds,
        notes
      });
      return response.data;
    } catch (error) {
      console.error('[strategyService] Error updating progress:', error);
      throw new Error(error.response?.data?.error || 'Error actualizando progreso');
    }
  },

  /**
   * Crear una nueva estrategia
   * @param {Object} strategyData Datos de la estrategia
   * @returns {Promise<Object>} Estrategia creada
   */
  createStrategy: async (strategyData) => {
    try {
      const response = await api.post('/strategy', strategyData);
      return response.data;
    } catch (error) {
      console.error('[strategyService] Error creating strategy:', error);
      throw new Error(error.response?.data?.error || 'Error creando estrategia');
    }
  },

  /**
   * Eliminar una estrategia
   * @param {string} strategyId ID de la estrategia
   * @returns {Promise<Object>} Resultado de la eliminación
   */
  deleteStrategy: async (strategyId) => {
    try {
      const response = await api.delete(`/strategy/${strategyId}`);
      return response.data;
    } catch (error) {
      console.error('[strategyService] Error deleting strategy:', error);
      throw new Error(error.response?.data?.error || 'Error eliminando estrategia');
    }
  },

  /**
   * Obtener una estrategia específica por ID
   * @param {string} strategyId ID de la estrategia
   * @returns {Promise<Object>} Estrategia encontrada
   */
  getStrategyById: async (strategyId) => {
    try {
      const strategies = await strategyService.getUserStrategies();
      const strategy = strategies.data.find(s => s.strategyId === strategyId);
      
      if (!strategy) {
        throw new Error('Estrategia no encontrada');
      }

      return {
        success: true,
        data: strategy
      };
    } catch (error) {
      console.error('[strategyService] Error fetching strategy:', error);
      throw new Error(error.response?.data?.error || 'Error obteniendo estrategia');
    }
  },

  /**
   * Obtener estadísticas de estrategias
   * @returns {Promise<Object>} Estadísticas
   */
  getStrategyStats: async () => {
    try {
      const strategies = await strategyService.getUserStrategies();
      const stats = {
        total: strategies.data.length,
        byStatus: {
          PLANNED: 0,
          IN_PROGRESS: 0,
          COMPLETED: 0
        },
        byCategory: {},
        averageProgress: 0
      };

      strategies.data.forEach(strategy => {
        // Contar por estado
        stats.byStatus[strategy.status]++;

        // Contar por categoría
        stats.byCategory[strategy.category] = (stats.byCategory[strategy.category] || 0) + 1;

        // Sumar progreso
        stats.averageProgress += strategy.progress || 0;
      });

      // Calcular promedio de progreso
      if (stats.total > 0) {
        stats.averageProgress = Math.round(stats.averageProgress / stats.total);
      }

      return {
        success: true,
        data: stats
      };
    } catch (error) {
      console.error('[strategyService] Error calculating stats:', error);
      throw new Error('Error calculando estadísticas');
    }
  },

  /**
   * Exportar estrategias a formato CSV
   * @param {Array<Object>} strategies Lista de estrategias
   * @returns {string} Contenido CSV
   */
  exportToCSV: (strategies) => {
    try {
      const headers = [
        'ID',
        'Título',
        'Descripción',
        'Categoría',
        'Estado',
        'Progreso',
        'Fecha Creación',
        'Última Modificación'
      ].join(',');

      const rows = strategies.map(strategy => [
        strategy.strategyId,
        `"${strategy.title.replace(/"/g, '""')}"`,
        `"${strategy.description.replace(/"/g, '""')}"`,
        strategy.category,
        strategy.status,
        strategy.progress,
        strategy.createdAt,
        strategy.lastModifiedAt
      ].join(','));

      return [headers, ...rows].join('\n');
    } catch (error) {
      console.error('[strategyService] Error exporting to CSV:', error);
      throw new Error('Error exportando a CSV');
    }
  }
};

export default strategyService;